import { ReactComponent as HefringShield } from "assets/hefring-shield.svg";
import { fuelViewColors, useFuelStatistics } from "hooks/use-fuel-statistics";
import { useTranslation } from "react-i18next";
import { useStore } from "hooks/use-store";
import { Color } from "utils/theme";
import { PLACEHOLDER } from "utils/constants";
import { Block } from "components/ui/block";
import { RecommendedSogType, RpmSogMode } from "types/enums";

export const FuelBlocks = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "fuel_curve",
  });
  const { t: tGlobal } = useTranslation();
  const sog = useStore((state) => state.sog);
  const rpm = useStore((state) => state.rpm);
  const rpmSogMode = useStore((state) => state.rpmSogMode);
  const {
    displayFuelRateNmi,
    displayFuelRateH,
    displayTripFuelRateNmi,
    fuelSavings,
    displayFuelSavings,
    hasRecommendedSog,
    recommendedSogType,
    recommendedSog,
    ecoSogRpm,
    isInEcoRange,
    isInEcoRangeRpm,
    currentSogColor,
    isFuelRateBad,
    isTripFuelRateBad,
    fuelCurveStatusMessage,
    safetySpeedLabel,
  } = useFuelStatistics();

  const isSogMode = rpmSogMode === RpmSogMode.Sog;

  const getRpmColor = () => {
    return isInEcoRangeRpm ? Color.Emerald[400] : undefined;
  };

  const getFuelRateColor = () => {
    if (isFuelRateBad) {
      return Color.Rose[600];
    }
    if (isInEcoRange) {
      return Color.Emerald[400];
    }
    return undefined;
  };

  const getTripFuelRateColor = () => {
    return isTripFuelRateBad ? Color.Rose[60] : undefined;
  };

  return (
    <div className="h-full w-full flex flex-wrap">
      <Block
        value={
          isSogMode
            ? sog !== null
              ? Math.round(sog)
              : PLACEHOLDER
            : rpm !== null
            ? Math.round(rpm)
            : PLACEHOLDER
        }
        valueColor={isSogMode ? currentSogColor : getRpmColor()}
        header={isSogMode ? t("sog") : tGlobal("dashboard.rpm")}
        footer={tGlobal("units.knots")}
        className="sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.3%-0.5rem)] m-1 w-full"
      />
      {isSogMode ? (
        <Block
          value={
            hasRecommendedSog ? (
              recommendedSog ?? PLACEHOLDER
            ) : fuelCurveStatusMessage ? (
              <p className="text-base" style={{ color: Color.White }}>
                {fuelCurveStatusMessage}
              </p>
            ) : (
              <div className="w-full flex justify-center items-center">
                <HefringShield className="h-full w-1/6 opacity-20" />
              </div>
            )
          }
          valueColor={fuelViewColors.Good}
          header={
            recommendedSogType === RecommendedSogType.Eco
              ? t("eco_speed")
              : safetySpeedLabel
          }
          footer={tGlobal("units.knots")}
          className="sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.3%-0.5rem)] m-1 w-full"
        />
      ) : (
        <Block
          value={
            ecoSogRpm ? (
              Math.round(ecoSogRpm / 100) * 100
            ) : fuelCurveStatusMessage ? (
              <p className="text-base" style={{ color: Color.White }}>
                {fuelCurveStatusMessage}
              </p>
            ) : (
              <div className="w-full flex justify-center items-center">
                <HefringShield className="h-full w-1/6 opacity-20" />
              </div>
            )
          }
          valueColor={fuelViewColors.Good}
          header={t("eco_rpm")}
          footer={"RPM"}
          className="sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.3%-0.5rem)] m-1 w-full"
        />
      )}
      <Block
        value={displayFuelRateNmi}
        valueColor={getFuelRateColor()}
        header={isFuelRateBad ? t("fuel_rate_exceeded") : t("fuel_rate")}
        footer={t("units.liters_per_nm")}
        className="sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.3%-0.5rem)] m-1 w-full"
      />
      <Block
        value={displayFuelSavings}
        valueColor={fuelSavings >= 0 ? Color.Emerald[400] : fuelViewColors.Bad}
        header={fuelSavings >= 0 ? t("fuel_savings") : t("fuel_excess")}
        footer={t("units.liters_per_trip")}
        className="sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.3%-0.5rem)] m-1 w-full"
      />
      <Block
        value={displayTripFuelRateNmi}
        valueColor={getTripFuelRateColor()}
        header={t("trip_average_fuel_rate")}
        footer={t("units.liters_per_nm")}
        className="sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.3%-0.5rem)] m-1 w-full"
      />
      <Block
        value={displayFuelRateH}
        valueColor={getTripFuelRateColor()}
        header={t("fuel_rate")}
        footer={t("units.liters_per_hour")}
        className="sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.3%-0.5rem)] m-1 w-full"
      />
    </div>
  );
};
